import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api, formatError, fmtDuration } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loading } from "@/components/Layout";
import { Plus, Trash, FloppyDisk, CalendarBlank, WarningCircle, WarningOctagon, ArrowsClockwise, Repeat, CaretDown, Television } from "@phosphor-icons/react";
import { toast } from "sonner";
import { EVENT_TYPE_META } from "@/pages/Programming";

const DAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
const HOURS = Array.from({ length: 24 }, (_, i) => i);
const TODAY = DAYS[(new Date().getUTCDay() + 6) % 7];
const RECURRENCE = [
  { v: "weekly", label: "Weekly (this day)" },
  { v: "daily", label: "Every day" },
  { v: "weekdays", label: "Weekdays" },
  { v: "weekends", label: "Weekends" },
];
const RECURRENCE_LABEL = { weekly: "Weekly", daily: "Daily", weekdays: "Mon–Fri", weekends: "Sat–Sun" };
function entryDays(e) {
  const r = e.recurrence || "weekly";
  if (r === "daily") return DAYS;
  if (r === "weekdays") return DAYS.slice(0, 5);
  if (r === "weekends") return DAYS.slice(5);
  return [e.day || "mon"];
}

export default function Scheduler() {
  const { user } = useAuth();
  const canEdit = ["owner", "admin", "operator"].includes(user?.role);
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [channels, setChannels] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [channelId, setChannelId] = useState("");
  const [day, setDay] = useState("mon");
  const [entries, setEntries] = useState(null);
  const [adder, setAdder] = useState({ block_id: "", start_time: "06:00", recurrence: "weekly" });
  const [saving, setSaving] = useState(false);
  const [validation, setValidation] = useState(null);
  const [nowPlaying, setNowPlaying] = useState(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [converting, setConverting] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/channels"), api.get("/blocks")]).then(([c, b]) => {
      setChannels(c.data); setBlocks(b.data);
      const sched = c.data.filter((x) => (x.mode || "scheduled") !== "loop");
      const pre = params.get("channel");
      const initial = pre && c.data.find((x) => x.id === pre) ? pre : sched[0]?.id || c.data[0]?.id || "";
      setChannelId(initial);
      if (b.data[0]) setAdder((a) => ({ ...a, block_id: b.data[0].id }));
    });
  }, []);

  useEffect(() => {
    if (!channelId) return;
    setEntries(null);
    api.get(`/channels/${channelId}/schedule`).then((r) => { setEntries(r.data.entries); setValidation(r.data.validation); });
  }, [channelId]);

  useEffect(() => {
    if (!channelId) return;
    const fetchNP = () => api.get(`/channels/${channelId}/now-playing`).then((r) => setNowPlaying(r.data)).catch(() => {});
    fetchNP();
    const t = setInterval(fetchNP, 5000);
    return () => clearInterval(t);
  }, [channelId]);

  const addEntry = () => {
    if (!adder.block_id) { toast.error("Select a block"); return; }
    const blk = blocks.find((b) => b.id === adder.block_id);
    setEntries([...entries, { block_id: adder.block_id, start_time: adder.start_time, day, recurrence: adder.recurrence, block: blk }]);
  };
  const removeEntry = (i) => setEntries(entries.filter((_, idx) => idx !== i));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { entries: entries.map((e) => ({ block_id: e.block_id, start_time: e.start_time, day: e.day, recurrence: e.recurrence || "weekly" })) };
      const { data } = await api.put(`/channels/${channelId}/schedule`, payload);
      setValidation(data.validation);
      toast.success("Schedule saved");
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const dayEntries = (entries || []).filter((e) => entryDays(e).includes(day));
  const toMinutes = (t) => { const [h, m] = t.split(":").map(Number); return h * 60 + m; };
  const nowMin = (() => { const d = new Date(); return d.getUTCHours() * 60 + d.getUTCMinutes(); })();
  const schedChannels = channels.filter((c) => (c.mode || "scheduled") !== "loop");
  const selectedChannel = channels.find((c) => c.id === channelId);
  const isLoop = selectedChannel && (selectedChannel.mode || "scheduled") === "loop";

  const convertToScheduled = async () => {
    setConverting(true);
    try {
      await api.post(`/channels/${channelId}/convert`, { mode: "scheduled" });
      const c = await api.get("/channels");
      setChannels(c.data);
      toast.success("Converted to Scheduled mode");
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setConverting(false); }
  };

  return (
    <>
      <PageHeader title="Scheduler" subtitle="Program blocks onto a 24-hour timeline"
        actions={canEdit && channelId && (
          <button data-testid="save-schedule-btn" onClick={save} disabled={saving}
            className="flex items-center gap-2 bg-[#3A86FF] hover:bg-[#2563EB] transition-colors duration-150 text-white text-sm font-medium px-4 py-2 rounded-sm disabled:opacity-60">
            <FloppyDisk size={16} /> {saving ? "Saving…" : "Save Schedule"}
          </button>
        )} />

      <div className="p-8 space-y-6 ccs-enter">
        {channels.length === 0 ? (
          <div className="text-center py-24 text-[#737373] text-sm">Create a channel first to build a schedule.</div>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-3">
              <select data-testid="scheduler-channel-select" value={channelId} onChange={(e) => setChannelId(e.target.value)}
                className="bg-[#141414] border border-[#262626] focus:border-[#3A86FF] rounded-sm px-3 py-2 text-sm outline-none min-w-[220px]">
                {channels.map((c) => <option key={c.id} value={c.id}>{c.name}{(c.mode || "scheduled") === "loop" ? " (Loop)" : ""}</option>)}
              </select>
              {!isLoop && (
                <div className="flex gap-1">
                  {DAYS.map((d) => (
                    <button key={d} data-testid={`day-${d}`} onClick={() => setDay(d)}
                      className={`px-3 py-2 text-xs font-mono uppercase rounded-sm border transition-colors duration-150 ${
                        day === d ? "border-[#3A86FF] bg-[#3A86FF]/10 text-[#3A86FF]" : "border-[#262626] text-[#A3A3A3] hover:border-[#3a3a3a]"
                      }`}>{d}</button>
                  ))}
                </div>
              )}
            </div>

            {isLoop ? (
              <div data-testid="loop-mode-note" className="bg-[#141414] border border-[#262626] rounded-sm p-10 text-center">
                <Repeat size={38} className="mx-auto mb-3 text-emerald-500" />
                <h3 className="font-head font-semibold text-base mb-1">Continuous Loop channel</h3>
                <p className="text-sm text-[#737373] max-w-md mx-auto mb-6">
                  This channel loops its content 24/7 and doesn't use scheduling. Manage its content from the channel page, or convert it to a Scheduled channel to unlock the timeline tools.
                </p>
                <div className="flex items-center justify-center gap-2">
                  <button data-testid="scheduler-open-channel" onClick={() => navigate(`/channels/${channelId}`)}
                    className="flex items-center gap-2 border border-[#262626] hover:border-[#3a3a3a] text-sm px-4 py-2 rounded-sm transition-colors duration-150">
                    <Television size={16} /> Open Channel
                  </button>
                  {canEdit && (
                    <button data-testid="convert-to-scheduled-btn" onClick={convertToScheduled} disabled={converting}
                      className="flex items-center gap-2 bg-[#3A86FF] hover:bg-[#2563EB] text-white text-sm px-4 py-2 rounded-sm transition-colors duration-150 disabled:opacity-60">
                      <ArrowsClockwise size={16} /> {converting ? "Converting…" : "Convert to Scheduled"}
                    </button>
                  )}
                </div>
              </div>
            ) : !entries ? <Loading /> : (
              <>
                <NowPlayingBar np={nowPlaying} />
                <AdvancedValidation v={validation} open={showAdvanced} onToggle={() => setShowAdvanced(!showAdvanced)} />
                {/* Timeline */}
                <div className="bg-[#141414] border border-[#262626] rounded-sm overflow-hidden">
                  <div className="overflow-x-auto">
                    <div className="min-w-[960px]">
                      <div className="flex border-b border-[#262626] sticky top-0 bg-[#141414]">
                        {HOURS.map((h) => (
                          <div key={h} className="flex-1 text-center py-1.5 font-mono text-[10px] text-[#525252] border-r border-[#1c1c1c] last:border-0">
                            {String(h).padStart(2, "0")}
                          </div>
                        ))}
                      </div>
                      <div className="relative h-24 bg-[#0A0A0A]">
                        {HOURS.map((h) => (
                          <div key={h} className="absolute top-0 bottom-0 border-r border-[#161616]" style={{ left: `${(h / 24) * 100}%` }} />
                        ))}
                        {dayEntries.map((e, i) => {
                          const start = toMinutes(e.start_time);
                          const dur = (e.block?.total_duration || 1800) / 60;
                          const left = (start / 1440) * 100;
                          const width = Math.max((dur / 1440) * 100, 2);
                          return (
                            <div key={i} data-testid={`timeline-block-${i}`}
                              className="absolute top-2 bottom-2 bg-violet-500/20 border border-violet-500/40 rounded-sm px-2 py-1 overflow-hidden hover:bg-violet-500/30 transition-colors duration-150"
                              style={{ left: `${left}%`, width: `${width}%` }}>
                              <div className="text-[10px] font-medium text-violet-200 truncate">{e.block?.name}</div>
                              <div className="font-mono text-[9px] text-violet-300/70 truncate">{e.start_time} · {fmtDuration(e.block?.total_duration || 0)}</div>
                            </div>
                          );
                        })}
                        {dayEntries.length === 0 && (
                          <div className="absolute inset-0 flex items-center justify-center text-xs text-[#525252]">No blocks scheduled on {day.toUpperCase()}</div>
                        )}
                        {day === TODAY && (
                          <div data-testid="timeline-playhead" className="absolute top-0 bottom-0 z-40 pointer-events-none" style={{ left: `${(nowMin / 1440) * 100}%` }}>
                            <div className="w-[1px] h-full bg-red-500" />
                            <div className="absolute -top-1 -left-[3px] h-2 w-2 rounded-full bg-red-500 animate-pulse" />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Adder + list */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {canEdit && (
                    <div className="bg-[#141414] border border-[#262626] rounded-sm p-5">
                      <h3 className="font-head font-semibold text-sm mb-4 flex items-center gap-2"><CalendarBlank size={16} className="text-[#3A86FF]" /> Add to {day.toUpperCase()}</h3>
                      <div className="space-y-3">
                        <div>
                          <label className="block text-[10px] uppercase tracking-[0.2em] text-[#737373] mb-1.5 font-bold">Block</label>
                          <select data-testid="adder-block-select" value={adder.block_id} onChange={(e) => setAdder({ ...adder, block_id: e.target.value })}
                            className="w-full bg-[#0A0A0A] border border-[#262626] focus:border-[#3A86FF] rounded-sm px-3 py-2 text-sm outline-none">
                            {blocks.length === 0 && <option value="">No blocks available</option>}
                            {blocks.map((b) => <option key={b.id} value={b.id}>{b.name} ({fmtDuration(b.total_duration)})</option>)}
                          </select>
                        </div>
                        <div>
                          <label className="block text-[10px] uppercase tracking-[0.2em] text-[#737373] mb-1.5 font-bold">Start time</label>
                          <input data-testid="adder-time-input" type="time" value={adder.start_time} onChange={(e) => setAdder({ ...adder, start_time: e.target.value })}
                            className="w-full bg-[#0A0A0A] border border-[#262626] focus:border-[#3A86FF] rounded-sm px-3 py-2 text-sm outline-none" />
                        </div>
                        <div>
                          <label className="block text-[10px] uppercase tracking-[0.2em] text-[#737373] mb-1.5 font-bold">Recurrence</label>
                          <select data-testid="adder-recurrence-select" value={adder.recurrence} onChange={(e) => setAdder({ ...adder, recurrence: e.target.value })}
                            className="w-full bg-[#0A0A0A] border border-[#262626] focus:border-[#3A86FF] rounded-sm px-3 py-2 text-sm outline-none">
                            {RECURRENCE.map((r) => <option key={r.v} value={r.v}>{r.label}</option>)}
                          </select>
                        </div>
                        <button data-testid="add-entry-btn" onClick={addEntry} disabled={blocks.length === 0}
                          className="w-full flex items-center justify-center gap-2 bg-[#3A86FF] hover:bg-[#2563EB] transition-colors duration-150 text-white text-sm font-medium py-2 rounded-sm disabled:opacity-60">
                          <Plus size={16} weight="bold" /> Add Block
                        </button>
                      </div>
                    </div>
                  )}

                  <div className="lg:col-span-2 bg-[#141414] border border-[#262626] rounded-sm">
                    <div className="px-5 py-3 border-b border-[#262626]"><h3 className="font-head font-semibold text-sm">{day.toUpperCase()} Line-up ({dayEntries.length})</h3></div>
                    {dayEntries.length === 0 ? (
                      <div className="p-8 text-center text-sm text-[#737373]">Nothing scheduled.</div>
                    ) : (
                      <div>
                        {[...dayEntries].sort((a, b) => toMinutes(a.start_time) - toMinutes(b.start_time)).map((e) => {
                          const idx = entries.indexOf(e);
                          return (
                            <div key={idx} data-testid={`lineup-entry-${idx}`} className="flex items-center gap-4 px-5 py-3 border-b border-[#1c1c1c] last:border-0">
                              <span className="font-mono text-sm text-[#3A86FF] w-14">{e.start_time}</span>
                              <div className="flex-1"><div className="text-sm">{e.block?.name}</div>
                                <div className="font-mono text-[10px] text-[#525252] flex items-center gap-2">
                                  {fmtDuration(e.block?.total_duration || 0)}
                                  <span className="flex items-center gap-1 text-[#737373]"><ArrowsClockwise size={10} /> {RECURRENCE_LABEL[e.recurrence || "weekly"]}</span>
                                </div></div>
                              {canEdit && <button data-testid={`remove-entry-${idx}`} onClick={() => removeEntry(idx)} className="text-[#737373] hover:text-red-500 p-1 transition-colors duration-150"><Trash size={15} /></button>}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </>
  );
}

function NowPlayingBar({ np }) {
  if (!np) return null;
  if (!np.on_air) {
    const reason = np.reason === "no_schedule" ? "no schedule configured" : np.reason === "offline" ? "channel is not live" : "schedule gap (dead air)";
    return (
      <div data-testid="now-playing-bar" className="bg-[#141414] border border-[#262626] rounded-sm px-5 py-3 flex items-center gap-3">
        <span className="h-2 w-2 rounded-full bg-neutral-600" />
        <span className="text-sm text-[#737373]">Not on air — {reason}</span>
      </div>
    );
  }
  const ev = np.event;
  const remain = np.remaining_in_event || 0;
  const pct = ev.duration_seconds ? ((ev.duration_seconds - remain) / ev.duration_seconds) * 100 : 0;
  const meta = EVENT_TYPE_META[ev.event_type] || EVENT_TYPE_META.content;
  return (
    <div data-testid="now-playing-bar" className="bg-[#141414] border border-emerald-500/30 rounded-sm px-5 py-3">
      <div className="flex items-center gap-3">
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-75" />
          <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
        </span>
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-emerald-500">On Air · Now Playing</span>
        <span className="ml-auto font-mono text-[10px] text-[#737373]">{np.block?.name} · block start {np.block_start}</span>
      </div>
      <div className="flex items-center gap-3 mt-2.5">
        <span className={`text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded-sm border ${meta.cls}`}>{meta.label}</span>
        <span className="text-sm font-medium truncate" data-testid="now-playing-title">{ev.title}</span>
        <span className="ml-auto font-mono text-xs text-[#A3A3A3]">-{fmtDuration(remain)}</span>
      </div>
      <div className="h-1 bg-[#0A0A0A] rounded-full mt-2 overflow-hidden">
        <div className="h-full bg-emerald-500 transition-all duration-1000" style={{ width: `${pct}%` }} />
      </div>
      {np.next_event && <div className="font-mono text-[10px] text-[#525252] mt-1.5">UP NEXT · {np.next_event.title}</div>}
    </div>
  );
}

function AdvancedValidation({ v, open, onToggle }) {
  if (!v) return null;
  const issues = (v.conflicts?.length || 0) + (v.gaps?.length || 0) + (v.warnings?.length || 0);
  const ok = issues === 0;
  const dot = ok ? "bg-emerald-500" : v.conflicts.length ? "bg-red-500" : "bg-amber-500";
  return (
    <div className="bg-[#141414] border border-[#262626] rounded-sm">
      <button data-testid="advanced-validation-toggle" onClick={onToggle} className="w-full flex items-center justify-between px-5 py-3">
        <div className="flex items-center gap-2">
          <span className={`h-2 w-2 rounded-full ${dot}`} />
          <span className="font-head font-semibold text-sm">Advanced Validation</span>
          <span className="font-mono text-[10px] text-[#737373]">{ok ? "All clear" : `${issues} item${issues > 1 ? "s" : ""} to review`}</span>
        </div>
        <CaretDown size={16} className={`text-[#737373] transition-transform duration-150 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="px-5 pb-5"><ValidationPanel v={v} /></div>}
    </div>
  );
}

function ValidationPanel({ v }) {
  if (!v) return null;
  const stats = [
    { label: "Conflicts", value: v.conflicts.length, bad: v.conflicts.length > 0, testid: "validation-conflicts", icon: WarningOctagon },
    { label: "Gaps (dead air)", value: v.gaps.length, warn: v.gaps.length > 0, testid: "validation-gaps", icon: WarningCircle },
    { label: "Warnings", value: v.warnings.length, warn: v.warnings.length > 0, testid: "validation-warnings", icon: WarningCircle },
  ];
  const details = [
    ...v.conflicts.map((c) => ({ kind: "conflict", text: `${c.day.toUpperCase()} · "${c.a}" overlaps "${c.b}" at ${c.at}` })),
    ...v.gaps.map((g) => ({ kind: "gap", text: `${g.day.toUpperCase()} · dead air ${g.start}–${g.end}` })),
    ...v.warnings.map((w) => ({ kind: "warn", text: w.message })),
  ];
  return (
    <div data-testid="validation-panel" className="space-y-3">
      <div className="grid grid-cols-3 gap-3">
        {stats.map((s) => (
          <div key={s.label} data-testid={s.testid}
            className={`bg-[#141414] border rounded-sm px-4 py-3 flex items-center gap-3 ${s.bad ? "border-red-500/40" : s.warn ? "border-amber-500/40" : "border-[#262626]"}`}>
            <s.icon size={18} className={s.bad ? "text-red-500" : s.warn ? "text-amber-500" : "text-emerald-500"} />
            <div>
              <div className="font-head font-bold text-lg tabular-nums leading-none">{s.value}</div>
              <div className="text-[10px] uppercase tracking-[0.15em] text-[#737373] mt-1">{s.label}</div>
            </div>
          </div>
        ))}
      </div>
      {details.length > 0 && (
        <div className="bg-[#141414] border border-[#262626] rounded-sm divide-y divide-[#1c1c1c]">
          {details.map((d, i) => (
            <div key={i} data-testid={`validation-detail-${i}`} className="flex items-center gap-2 px-4 py-2 text-xs">
              <span className={`h-1.5 w-1.5 rounded-full ${d.kind === "conflict" ? "bg-red-500" : d.kind === "gap" ? "bg-amber-500" : "bg-amber-400"}`} />
              <span className="text-[#A3A3A3]">{d.text}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
